import React from 'react';
import { ArrowRight, Globe, Users, Zap, ArrowLeft, Beaker } from 'lucide-react';

interface ManifestoProps {
  onNavigateToPractice: () => void;
  onBack: () => void;
}

const Manifesto: React.FC<ManifestoProps> = ({ onNavigateToPractice, onBack }) => {
  return (
    <section className="min-h-screen pt-32 pb-24 md:pt-48 md:pb-40 bg-cream text-charcoal px-6 overflow-hidden relative">
      <div className="max-w-6xl mx-auto relative z-10">
        <button 
          onClick={onBack}
          className="mb-12 flex items-center gap-2 text-neutral-400 hover:text-terracotta transition-colors group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em]">Return to Salon</span>
        </button>

        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7 space-y-16">
            <div className="space-y-6">
              <span className="text-terracotta text-[10px] font-black uppercase tracking-[0.5em] block">Our Philosophy</span>
              <h1 className="text-6xl md:text-[7rem] leading-[0.85] tracking-tighter font-black text-charcoal">
                The Human <br/>
                <span className="text-neutral-400 italic">Response</span> <br/>
                to AI.
              </h1>
            </div>
            
            <div className="max-w-xl space-y-10 text-xl md:text-2xl text-neutral-800 leading-relaxed font-light">
              <p>
                In an era defined by algorithms, we believe the most valuable asset is not data, but human perspective. 
              </p>
              <p>
                Vibe with AI was born from a simple realization: while technology is scaling at an exponential rate, 
                our collective ability to make sense of it is not.
              </p>
              <p className="border-l-4 border-terracotta pl-8 py-2 italic text-charcoal font-medium">
                We operate as a dual-natured organization, powered by the technical R&D of <a href="https://labz.io" target="_blank" rel="noopener noreferrer" className="underline decoration-neutral-200 hover:decoration-terracotta transition-all">Lab Z</a>.
              </p>
              <p>
                Through <span className="text-charcoal font-bold underline decoration-terracotta underline-offset-4">The Salon</span>, we curate the culture and connection needed to understand our new reality.
              </p>
              <p>
                Through <span className="text-charcoal font-bold underline decoration-terracotta underline-offset-4">The Practice</span>, we provide high-level strategic advisory informed by real-world experimentation.
              </p>
              
              <div className="pt-10">
                <button 
                  onClick={onNavigateToPractice}
                  className="group flex items-center justify-between gap-6 bg-terracotta text-white px-8 py-5 rounded-lg shadow-[0_15px_30px_-10px_rgba(192,108,93,0.4)] hover:bg-[#A85B4E] hover:scale-[1.02] transition-all w-full md:w-auto"
                >
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Explore Our Advisory Practice</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-48 space-y-12">
            <div className="bg-white border border-neutral-200 p-10 md:p-14 rounded-2xl shadow-sm space-y-12">
              <div className="space-y-8">
                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-terracotta">Legacy of Curation</h3>
                <div className="space-y-6">
                  <div className="flex gap-5 items-start">
                    <Users size={20} className="text-neutral-300 mt-1 shrink-0" />
                    <div>
                      <p className="text-sm text-charcoal leading-relaxed uppercase tracking-widest font-bold mb-1">
                        15+ Years Experience
                      </p>
                      <p className="text-xs text-neutral-500 leading-relaxed font-medium">
                        Crafting spaces for high-stakes idea exchange and communal learning.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-5 items-start">
                    <Beaker size={20} className="text-neutral-300 mt-1 shrink-0" />
                    <div>
                      <p className="text-sm text-charcoal leading-relaxed uppercase tracking-widest font-bold mb-1">
                        Lab Z Innovation
                      </p>
                      <p className="text-xs text-neutral-500 leading-relaxed font-medium">
                        Insights grounded in the continuous R&D and rapid prototyping done at Lab Z.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-5 items-start">
                    <Globe size={20} className="text-neutral-300 mt-1 shrink-0" />
                    <div>
                      <p className="text-sm text-charcoal leading-relaxed uppercase tracking-widest font-bold mb-1">
                        TED heritage
                      </p>
                      <p className="text-xs text-neutral-500 leading-relaxed font-medium">
                        Led by TEDster and TEDxNaperville founder Arthur Zards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-10 border-t border-neutral-100">
                <blockquote className="text-2xl font-bold tracking-tight mb-8 italic text-neutral-700 leading-snug">
                  "Our mission is to ensure that as machines get smarter, humans don't get quieter."
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="h-0.5 w-10 bg-terracotta" />
                  <span className="text-[10px] uppercase tracking-[0.4em] font-black text-neutral-400">Arthur Zards, Founder</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute -bottom-20 -left-20 text-[20rem] font-black text-neutral-900/[0.03] select-none pointer-events-none leading-none -rotate-6">
        MANIFESTO
      </div>
    </section>
  );
};

export default Manifesto;